import * as React from "react"
import { useSubscriptions } from "../accounts/api/get-subscriptions";
import SubscriptionsTable from "../subscriptions/components/SubscriptionsTable";
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { startOfMonth, addMonths, format } from "date-fns"
import { exportSubscriptions } from "../accounts/api/export-subscriptions";
import { Button } from "@/components/ui/button";

export default function MonthlyCommissionReport() {
    const monthOptions = React.useMemo(() => {
        const options: { value: string; label: string; date: Date }[] = []
        const now = new Date()
        const currentMonth = now.getMonth()
        const currentYear = now.getFullYear()
        
        // Start from last year's current month, go up to current month
        for (let i = 0; i <= 12; i++) {
            const date = new Date(currentYear - 1, currentMonth + i, 1)
            options.push({
                value: `${date.getFullYear()}-${date.getMonth()}`,
                label: format(date, 'MMMM yyyy'),
                date,
            })
        }
        return options
    }, [])

    // Default to previous month
    const defaultValue = React.useMemo(() => {
        const lastMonth = addMonths(new Date(), -1)
        return `${lastMonth.getFullYear()}-${lastMonth.getMonth()}`
    }, [])

    const [selectedValue, setSelectedValue] = React.useState<string>(defaultValue)
    
    const from = React.useMemo(() => {
        const [year, month] = selectedValue.split('-').map(Number)
        return startOfMonth(new Date(year, month, 1))
    }, [selectedValue])
    const to = React.useMemo(() => startOfMonth(addMonths(from, 1)), [from])

    const { data: subscriptions, isLoading } = useSubscriptions({
        params: {
            status: 'active',
            currentPeriodStart: {
                from,
                to,
            },
            commonQuery: {
                includeTotals: true,
                sort: 'currentPeriodStart',
                page: 1,
                size: 1000,
            },
        },
    })

    return (
        <div className='space-y-4'>
            <div className='w-64'>
                <div className='flex flex-col gap-1'>
                    <Label htmlFor='report-month'>Month</Label>
                    <Select value={selectedValue} onValueChange={setSelectedValue}>
                        <SelectTrigger id='report-month'>
                            <SelectValue placeholder='Select month' />
                        </SelectTrigger>
                        <SelectContent>
                            {monthOptions.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <div className='text-sm text-muted-foreground'>
                        Showing subscriptions for {format(from, 'LLLL yyyy')}
                    </div>
                </div>
                <Button onClick={() => exportSubscriptions({
                    status: 'active',
                    currentPeriodStart: {
                        from,
                        to,
                    },
                })}>Export</Button>
            </div>

            <SubscriptionsTable
                data={subscriptions?.items || []}
                totals={subscriptions?.totals}
                pageIndex={0}
                pageSize={1000}
                pageCount={1}
                onPaginationChange={() => { }}
                sorting={[]}
                onSortingChange={() => { }}
                accountId={''}
                onAccountChange={() => { }}
                scope={'all'}
                onScopeChange={() => { }}
                renewalRange={{ from: null, to: null }}
                onRenewalRangeChange={() => { }}
                search={''}
                onSearchChange={() => { }}
                hideFilters={true}
                isLoading={isLoading}
                showRowCountColumn={true}
            />
        </div>
    )
}